import { Panel, Text, Title, Icon } from "components/commons";
import React, { useState } from "react";
import lang from "translations";
import { formatTagUIds } from "services";

// order | guest | date | paid | credit | free credit | voucher | add free credit | chevron
const GRID_COLUMNS = "60px 1.6fr 1.2fr 1fr 1fr 1fr 1fr 1fr 24px";

const Cell = ({ cell, strong, positive }) => (
  <div className="text-right">
    <Text
      size={strong ? "text-md" : "text-sm"}
      strong={strong}
      color={cell.isZero ? "text-gray-light" : positive ? "text-green-darkest" : "text-black"}
      className={positive && !cell.isZero ? "font-semibold" : undefined}
    >
      {cell.text}
    </Text>
  </div>
);

const DetailLine = ({ label, value, valueColor = "text-black", strike }) => (
  <div className="flex items-center justify-between gap-md py-xs">
    {typeof label === "string" ? (
      <Text size="text-sm" color="text-gray">
        {label}
      </Text>
    ) : (
      label
    )}
    <Text
      size="text-sm"
      strong
      color={strike ? "text-gray-light" : valueColor}
      className={`flex-shrink-0 whitespace-nowrap ${strike ? "line-through" : ""}`}
    >
      {value}
    </Text>
  </div>
);

const DetailCard = ({ title, children }) => (
  <div className="mb-md">
    <div className="mb-sm">
      <Text size="text-xs" strong uppercase color="text-blue" className="tracking-wider">
        {title}
      </Text>
    </div>
    <div className="rounded border border-gray-border bg-white px-md py-sm">{children}</div>
  </div>
);

const BalanceCard = ({ title, balance }) => (
  <DetailCard title={title}>
    <DetailLine label={lang.credits} value={balance.credits} />
    <DetailLine label={lang.freeCredits} value={balance.freeCredits} />
    <DetailLine label={lang.postpaidCreditsUsed} value={balance.postpaidCreditsUsed} />
  </DetailCard>
);

/**
 * Display-only "Payment Details" table for split payments, mapped from
 * `soarPaymentDetails` (built from `transaction.transactionPaymentDetails`).
 * Click a row to expand its previous balance / total paid / voucher used /
 * guest's new balance breakdown.
 */
const SplitPayment = ({ paymentDetails }) => {
  const [expanded, setExpanded] = useState({});

  if (!paymentDetails?.rows?.length) {
    return null;
  }
  const { rows, total, summary } = paymentDetails;
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const headers = [
    lang.paymentDetailsOrder,
    lang.guest,
    lang.date,
    lang.paidAmount,
    lang.fromCredit,
    lang.fromFreeCredit,
    lang.byVoucher,
    lang.addFreeCredit,
    "",
  ];

  const summaryCards = [
    { label: lang.totalBillToPay, value: summary.totalBill, color: "text-pelorous" },
    { label: lang.numberOfSplitPayments, value: summary.splitCount, color: "text-black" },
    { label: lang.totalCollected, value: summary.totalCollected, color: "text-black" },
  ];

  return (
    <>
    <div className="mb-md grid grid-cols-1 gap-md md:grid-cols-3">
      {summaryCards.map((card) => (
        <div key={card.label} className="rounded-lg bg-white p-lg shadow">
          <Text uppercase strong size="text-xs" color="text-gray">
            {card.label}
          </Text>
          <Text strong size="text-xl" color={card.color}>
            {card.value}
          </Text>
        </div>
      ))}
    </div>
    <Panel>
      <div className="flex items-center justify-between">
        <Title>{lang.paymentDetails}</Title>
        <span className="rounded-full bg-gray-disabled px-md py-xs">
          <Text size="text-xs" strong color="text-gray">
            {lang.clickRowToExpand}
          </Text>
        </span>
      </div>

      <div className="-mx-lg mt-md overflow-x-auto">
        <div style={{ minWidth: "820px" }}>
          <div
            className="grid items-center gap-2 bg-gray-lightest px-lg py-sm"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            {headers.map((header, i) => (
              <div key={i} className={i >= 3 && i <= 7 ? "text-right" : ""}>
                <Text uppercase strong size="text-xs" color="text-gray">
                  {header}
                </Text>
              </div>
            ))}
          </div>

          {rows.map((row) => {
            const isOpen = Boolean(expanded[row.id]);
            return (
              <div key={row.id} className="border-t border-gray-lightest">
                <div
                  className={`grid cursor-pointer items-center gap-2 px-lg py-md ${
                    isOpen ? "bg-blue-opaque" : ""
                  }`}
                  style={{ gridTemplateColumns: GRID_COLUMNS }}
                  onClick={() => toggle(row.id)}
                >
                  <div>
                    <span className="inline-block rounded bg-blue-opaque px-sm py-xs">
                      <Text size="text-xs" strong color="text-pelorous">
                        {row.order}
                      </Text>
                    </span>
                  </div>
                  <div>
                    <Text strong>{row.guestName}</Text>
                    <Text size="text-xs" color="text-gray">
                      {formatTagUIds(row.tagUid)}
                    </Text>
                    {row.tap && (
                      <Text size="text-xs" color="text-gray">
                        {row.tap}
                      </Text>
                    )}
                  </div>
                  <div>
                    <Text>{row.dateTime}</Text>
                  </div>
                  <Cell cell={row.paidAmount} strong />
                  <Cell cell={row.fromCredit} />
                  <Cell cell={row.fromFreeCredit} />
                  <Cell cell={row.byVoucher} />
                  <Cell cell={row.addFreeCredit} positive />
                  <span
                    className={`inline-flex justify-center text-gray transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  >
                    <Icon name="arrow-down" fontSize="10px" />
                  </span>
                </div>

                {isOpen && (
                  <div className="bg-gray-lightest px-lg py-md">
                    <div className="w-full md:w-2/3 lg:w-1/2">
                      <BalanceCard title={lang.previousBalance} balance={row.previousBalance} />
                      <BalanceCard title={lang.totalPaid} balance={row.totalPaid} />
                      {row.vouchers.length > 0 && (
                        <DetailCard title={lang.voucherUsed}>
                          <Text size="text-sm" color="text-gray">
                            {lang.voucher}:
                          </Text>
                          {row.vouchers.map((voucher, i) => (
                            <DetailLine
                              key={i}
                              label={<Text strong>{voucher.label}</Text>}
                              value={voucher.amount}
                              strike
                            />
                          ))}
                          <div className="my-sm border-t border-gray-border" />
                          <DetailLine
                            label={lang.paidByAppliedVoucher}
                            value={row.paidByAppliedVoucher}
                          />
                          <DetailLine
                            label={lang.returnFreeCreditByAppliedVoucher}
                            value={row.returnedFreeCredit.text}
                            valueColor={
                              row.returnedFreeCredit.isZero ? "text-black" : "text-green-darkest"
                            }
                          />
                        </DetailCard>
                      )}
                      <BalanceCard title={lang.guestsNewBalance} balance={row.newBalance} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div
            className="grid items-center gap-2 border-t border-gray-border bg-gray-lightest px-lg py-md"
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div className="col-span-3">
              <Text uppercase strong size="text-xs" color="text-gray">
                {lang.total}
              </Text>
            </div>
            <Cell cell={total.paidAmount} strong />
            <Cell cell={total.fromCredit} strong />
            <Cell cell={total.fromFreeCredit} strong />
            <Cell cell={total.byVoucher} strong />
            <Cell cell={total.addFreeCredit} strong positive />
            <div />
          </div>
        </div>
      </div>
    </Panel>
    </>
  );
};

export default SplitPayment;
